import React, { useState, useEffect, useCallback } from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Box,
  Typography,
  Tooltip,
  Paper,
  Divider,
  Menu,
  MenuItem,
  ListItemButton
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  CreateNewFolder as CreateNewFolderIcon
} from '@mui/icons-material';
import './FolderTree.css';
import { toast } from 'react-toastify';
import axios from "axios";
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';

// Açık klasörleri takip etmek için global bir değişken
const openFolderState = {};

const FolderTreeItem = React.memo(({ 
  folder, 
  selectedFolderId, 
  onFolderSelect, 
  onDeleteFolder,
  onAddFolder,
  onEditFolder,
  level = 0,
  openFolders,
  setOpenFolders
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(openFolderState[folder.id] || false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const hasChildren = folder.children && folder.children.length > 0;
  const isSelected = selectedFolderId === folder.id;
  const isMaxLevel = level >= 2;

  useEffect(() => {
    openFolderState[folder.id] = open;
  }, [open, folder.id]);

  useEffect(() => {
    if (isSelected && !open && hasChildren) {
      setOpen(true);
      openFolderState[folder.id] = true;
    }
  }, [isSelected, open, hasChildren, folder.id]);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    setOpen(!open);
    openFolderState[folder.id] = !open;
  }, [open, folder.id]);

  const handleSelect = useCallback((e) => {
    e.stopPropagation();
    onFolderSelect(folder);
  }, [folder, onFolderSelect]);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    handleMenuClose();
    if (window.confirm(t('delete_folder_confirm', { folderName: folder.title }))) {
      onDeleteFolder(folder.id);
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    handleMenuClose();
    onEditFolder(folder.id, folder.title);
  };

  const handleAddSubfolder = (e) => {
    e.stopPropagation();
    handleMenuClose();
    onAddFolder(folder.id);
  };

  return (
    <>
      <Box sx={{ 
        pl: level * 2,
        mb: 0.5
      }}>
        <Box
          onClick={handleSelect}
          sx={{
            p: 1.5,
            borderRadius: '8px',
            bgcolor: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
            border: isSelected ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              bgcolor: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'rgba(0, 0, 0, 0.04)',
              borderColor: isSelected ? 'rgba(59, 130, 246, 0.3)' : 'rgba(0, 0, 0, 0.1)'
            }
          }}
          className={`folder-item ${open ? 'open' : ''}`}
        >
          {/* Toggle Button */}
          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={handleToggle}
                sx={{ 
                  p: 0.5,
                  color: isSelected ? '#3B82F6' : '#6B7280',
                  '&:hover': {
                    bgcolor: 'rgba(59, 130, 246, 0.1)'
                  }
                }}
                className="folder-toggle-button"
              >
                {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 24 }} />
            )}
          </Box>
          
          {/* Folder Icon */}
          <Box sx={{ 
            mr: 1.5,
            color: isSelected ? '#3B82F6' : '#6B7280'
          }}>
            {isSelected || open ? (
              <FolderOpenIcon fontSize="small" />
            ) : (
              <FolderIcon fontSize="small" />
            )}
          </Box>
          
          {/* Folder Title */}
          <Typography 
            variant="body2" 
            sx={{ 
              flexGrow: 1,
              fontWeight: isSelected ? 600 : 500,
              color: isSelected ? '#1E40AF' : '#374151',
              fontSize: '0.875rem',
              noWrap: true,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
            title={folder.title}
          >
            {folder.title}
          </Typography>
          
          {/* Menu Button */}
          <Tooltip title="Klasör işlemleri">
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{ 
                opacity: 0.6,
                p: 0.5,
                color: '#6B7280',
                '&:hover': { 
                  opacity: 1,
                  bgcolor: 'rgba(0, 0, 0, 0.04)'
                } 
              }}
              className="folder-menu-button"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Alt klasörler */}
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {folder.children.map((child) => (
              <FolderTreeItem
                key={child.id}
                folder={child}
                selectedFolderId={selectedFolderId}
                onFolderSelect={onFolderSelect}
                onDeleteFolder={onDeleteFolder}
                onAddFolder={onAddFolder}
                onEditFolder={onEditFolder}
                level={level + 1}
                openFolders={openFolders}
                setOpenFolders={setOpenFolders}
              />
            ))}
          </List>
        </Collapse>
      )}
      
      {/* Klasör işlemleri menüsü */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 180 }
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {!isMaxLevel && (
          <MenuItem onClick={handleAddSubfolder}>
            <ListItemIcon>
              <CreateNewFolderIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('add_subfolder')} />
          </MenuItem>
        )}
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('Klasör Adını Düzenle')} />
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary={t('delete_folder')} />
        </MenuItem>
      </Menu>
    </>
  );
});

const FolderTree = React.memo(({ 
  folders, 
  selectedFolderId, 
  onFolderSelect, 
  onDeleteFolder,
  onAddFolder,
  onEditFolder 
}) => {
  const { t } = useTranslation();
  const [openFolders, setOpenFolders] = useState({});

  const handleRootFolderSelect = useCallback((e) => {
    e.stopPropagation();
    onFolderSelect({ id: 0, title: t('main_folder') });
  }, [onFolderSelect, t]);

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Ana Klasör */}
      <Box sx={{ 
        mb: 2,
        p: 1.5,
        borderRadius: '8px',
        bgcolor: selectedFolderId === 0 ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
        border: selectedFolderId === 0 ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid transparent',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: selectedFolderId === 0 ? 'rgba(59, 130, 246, 0.1)' : 'rgba(0, 0, 0, 0.04)',
          borderColor: selectedFolderId === 0 ? 'rgba(59, 130, 246, 0.3)' : 'rgba(0, 0, 0, 0.1)'
        }
      }}
      onClick={handleRootFolderSelect}
      className="folder-item root-folder"
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ 
          mr: 1.5,
          color: selectedFolderId === 0 ? '#3B82F6' : '#6B7280'
        }}>
          <FolderOpenIcon fontSize="small" />
        </Box>
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: selectedFolderId === 0 ? 600 : 500,
            color: selectedFolderId === 0 ? '#1E40AF' : '#374151',
            fontSize: '0.875rem'
          }}
        >
          Ana Klasör
        </Typography>
      </Box>
    </Box>
      
    <List component="nav" dense className="folder-list" sx={{ flexGrow: 1, overflow: 'auto' }}>
      {folders.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            Henüz klasör oluşturulmamış
          </Typography>
        </Box>
      ) : (
        folders.map((folder) => (
          <FolderTreeItem
            key={folder.id}
            folder={folder}
            selectedFolderId={selectedFolderId}
            onFolderSelect={onFolderSelect}
            onDeleteFolder={onDeleteFolder}
            onAddFolder={onAddFolder}
            onEditFolder={onEditFolder}
            openFolders={openFolders}
            setOpenFolders={setOpenFolders}
          />
        ))
      )}
    </List>
    </Box>
  );
});

export default FolderTree;